// Env-suffixed like every cookie key in auth-config.ts — Web Locks are scoped
// per-origin, so this only matters if two envs ever shared one, but it costs
// nothing to match the convention.
const ENV = process.env.NEXT_PUBLIC_ENV || 'staging'
const REFRESH_TOKEN_LOCK_NAME = `oems-refresh-token-${ENV}`

// Bounds how long a caller waits to ACQUIRE the lock — NOT the wrapped call
// itself. Once granted, `fn` always runs to completion unaborted: the refresh
// token is single-use, so cancelling an in-flight rotation can burn it before
// the browser ever sees the rotated cookie in the response, forcing the exact
// false "suspicious sign-in" flow this lock exists to prevent (the same
// lesson is called out in auth-utils.server.ts for the Server Component case).
// A caller whose wait times out gets AbortError and should treat it as
// transient, not as a rejection of the refresh itself.
export const REFRESH_TOKEN_LOCK_WAIT_TIMEOUT_MS = 15_000

function abortAfterWait(): AbortSignal | undefined {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(REFRESH_TOKEN_LOCK_WAIT_TIMEOUT_MS)
    : undefined
}

/**
 * Serializes every /api/auth/refresh call across tabs and call sites. Web
 * Locks are scoped per-origin, so a caller that waits out another tab's
 * in-flight refresh sees the browser's already-rotated cookie by its turn
 * instead of replaying a stale token — which is what trips the backend's
 * reuse-based theft detection. Falls back to unlocked when Web Locks isn't
 * available (today's pre-fix behavior, not a regression).
 */
export async function withRefreshTokenLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return fn()
  }
  const signal = abortAfterWait()
  // `await`, not a bare return: lib.dom types the callback as returning T
  // (not PromiseLike<T>), so TS would otherwise infer T = Promise<OurT> and
  // double-wrap the result.
  return await navigator.locks.request(REFRESH_TOKEN_LOCK_NAME, signal ? { signal } : {}, fn)
}
